'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { useSession } from 'next-auth/react';
type TUseAccessToken = {
  getAccessToken: () => Promise<Maybe<string>>;
  refreshAndGetAccessToken: () => Promise<Maybe<string>>;
};

/**
 * Hook that exposes access token from session.
 *
 * `getAccessToken` reads from a ref so it always returns the latest token,
 * even when called from stale closures (e.g. React Query retries).
 *
 * `refreshAndGetAccessToken` forces server-side refresh and returns fresh token directly.
 */
export function useAccessToken() {
  const $ = _c(8);
  const {
    data: session,
    update
  } = useSession();
  const sessionRef = useRef(session);
  let t0;
  let t1;
  if ($[0] !== session) {
    t0 = () => {
      sessionRef.current = session;
    };
    t1 = [session];
    $[0] = session;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async () => sessionRef.current?.accessToken ?? null;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const getAccessToken = t2;
  let t3;
  if ($[4] !== update) {
    t3 = async () => {
      const fresh = await update();
      sessionRef.current = fresh;
      return fresh?.accessToken ?? null;
    };
    $[4] = update;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const refreshAndGetAccessToken = t3;
  let t4;
  if ($[6] !== refreshAndGetAccessToken) {
    t4 = {
      getAccessToken,
      refreshAndGetAccessToken
    };
    $[6] = refreshAndGetAccessToken;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}